import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Search,
  ArrowRight,
  User,
  ListChecks,
  History,
  AlertTriangle,
  GitCompare,
} from "lucide-react";
import { GetMatchPairComparison } from "./apis/get";

interface Person {
  found: boolean;
  user_id: string;
  name?: string;
  gender?: string;
  age?: number | null;
  city?: string;
  state?: string;
  occupation?: string;
  caste?: string;
  manglik?: string;
  email?: string;
  phone?: string;
  assigned_to?: string | null;
  credit_balance?: number | null;
  is_active?: string;
}

interface Direction {
  direction: string;
  user_id: string;
  candidate_id: string;
  matched_profile: Record<string, any> | null;
  queue: Record<string, any>[];
  audit: Record<string, any>[];
}

interface PairData {
  people: { a: Person; b: Person };
  forward: Direction;
  reverse: Direction;
}

// Fields shown (in order) in the side-by-side matched_profiles comparison
const MATCH_FIELDS: { key: string; label: string }[] = [
  { key: "id", label: "Row ID" },
  { key: "match_score", label: "Match Score" },
  { key: "compatibility_level", label: "Compatibility" },
  { key: "family_values_score", label: "Family Values Score" },
  { key: "proximity", label: "Proximity (km)" },
  { key: "published", label: "Published" },
  { key: "published_at", label: "Published At" },
  { key: "decision", label: "Decision" },
  { key: "is_disliked", label: "Disliked" },
  { key: "is_viewed", label: "Viewed" },
  { key: "is_notified", label: "Notified" },
  { key: "is_reported", label: "Reported" },
  { key: "is_blocked", label: "Blocked" },
  { key: "chat_consent", label: "Chat Consent" },
  { key: "is_acknowledged", label: "Acknowledged" },
  { key: "algorithm_version", label: "Algorithm Version" },
  { key: "source_service", label: "Source Service" },
  { key: "is_active", label: "Active" },
  { key: "created_at", label: "Created At" },
  { key: "updated_at", label: "Updated At" },
];

const fmt = (v: any) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
};

const fmtDate = (v: any) => {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v) : d.toLocaleString();
};

function Badge({ children, tone = "slate" }: { children: React.ReactNode; tone?: string }) {
  const tones: Record<string, string> = {
    slate: "bg-slate-700/40 text-slate-300 border-slate-600/50",
    green: "bg-emerald-600/20 text-emerald-300 border-emerald-600/40",
    red: "bg-rose-600/20 text-rose-300 border-rose-600/40",
    blue: "bg-indigo-600/20 text-indigo-300 border-indigo-600/40",
    amber: "bg-amber-600/20 text-amber-300 border-amber-600/40",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${tones[tone] || tones.slate}`}>
      {children}
    </span>
  );
}

function PersonCard({ label, person }: { label: string; person: Person }) {
  if (!person?.found) {
    return (
      <div className="flex-1 bg-slate-900/60 border border-slate-800 rounded-2xl p-4">
        <div className="text-xs text-slate-500 mb-1">{label}</div>
        <div className="text-slate-400 flex items-center gap-2">
          <AlertTriangle size={16} className="text-amber-400" />
          {person?.user_id || "Unknown"} — not found
        </div>
      </div>
    );
  }
  return (
    <div className="flex-1 bg-slate-900/60 border border-slate-800 rounded-2xl p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs text-slate-500">{label}</div>
        <Badge tone={person.is_active === "Active" ? "green" : "red"}>{person.is_active}</Badge>
      </div>
      <div className="flex items-center gap-2 text-lg font-semibold text-slate-100">
        <User size={18} className="text-indigo-400" />
        {person.name}
      </div>
      <div className="text-xs text-indigo-300/80 mb-3 font-mono">{person.user_id}</div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-slate-300">
        <span className="text-slate-500">Gender</span><span>{fmt(person.gender)}</span>
        <span className="text-slate-500">Age</span><span>{fmt(person.age)}</span>
        <span className="text-slate-500">Location</span><span>{fmt(person.city)}, {fmt(person.state)}</span>
        <span className="text-slate-500">Occupation</span><span>{fmt(person.occupation)}</span>
        <span className="text-slate-500">Caste</span><span>{fmt(person.caste)}</span>
        <span className="text-slate-500">Manglik</span><span>{fmt(person.manglik)}</span>
        <span className="text-slate-500">Phone</span><span>{fmt(person.phone)}</span>
        <span className="text-slate-500">Email</span><span className="truncate">{fmt(person.email)}</span>
        <span className="text-slate-500">Assigned To</span><span>{fmt(person.assigned_to)}</span>
        <span className="text-slate-500">Credits</span><span>{fmt(person.credit_balance)}</span>
      </div>
    </div>
  );
}

function QueueBlock({ rows }: { rows: Record<string, any>[] }) {
  if (!rows?.length) return <div className="text-sm text-slate-500 italic">No queue rows.</div>;
  return (
    <div className="space-y-3">
      {rows.map((q) => (
        <div key={q.id} className="bg-slate-950/60 border border-slate-800 rounded-xl p-3 text-sm">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="font-mono text-xs text-slate-500">#{q.id}</span>
            {q.is_matched && <Badge tone="green">matched</Badge>}
            {q.is_in_queue && <Badge tone="blue">in queue</Badge>}
            {q.is_processing && <Badge tone="amber">processing</Badge>}
            {q.force_to_match && <Badge tone="amber">forced</Badge>}
            {q.requested && <Badge tone="blue">requested</Badge>}
            {q.has_error && <Badge tone="red">error</Badge>}
            {!q.is_active && <Badge tone="red">inactive</Badge>}
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-slate-300">
            <span className="text-slate-500">Source</span><span>{fmt(q.source_service)}</span>
            <span className="text-slate-500">Reason</span><span>{fmt(q.reason)}</span>
            <span className="text-slate-500">LLM Score</span><span>{fmt(q.llm_score)}</span>
            <span className="text-slate-500">Run Count</span><span>{fmt(q.run_count)}</span>
            <span className="text-slate-500">Priority</span><span>{fmt(q.priority)}</span>
            <span className="text-slate-500">Created</span><span>{fmtDate(q.created_at)}</span>
          </div>
          {q.error_message && <div className="mt-2 text-rose-300 text-xs">{q.error_message}</div>}
          {q.llm_commentary && (
            <div className="mt-2 text-slate-400 text-xs whitespace-pre-wrap border-t border-slate-800 pt-2">
              {q.llm_commentary}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const OP_TONE: Record<string, string> = {
  insert: "green",
  update: "blue",
  reactivate: "amber",
  deactivate: "red",
};

function AuditTimeline({ rows }: { rows: Record<string, any>[] }) {
  if (!rows?.length) return <div className="text-sm text-slate-500 italic">No audit records.</div>;
  return (
    <ol className="relative border-l border-slate-800 ml-2 space-y-4">
      {rows.map((r) => (
        <li key={r.id} className="ml-4">
          <div className="absolute -left-1.5 w-3 h-3 rounded-full bg-indigo-500 border border-slate-950" />
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={OP_TONE[r.operation] || "slate"}>{r.operation}</Badge>
            <span className="text-xs text-slate-500">{fmtDate(r.created_at)}</span>
            <Badge>{r.target_table}</Badge>
          </div>
          <div className="mt-1 text-sm text-slate-300">
            <span className="text-slate-500">by </span>
            <span className="font-medium">{r.actor_type || "unknown"}</span>
            {r.actor_id && <span className="text-slate-400"> ({r.actor_id})</span>}
            {r.source_service && (
              <>
                <span className="text-slate-500"> via </span>
                <span className="text-indigo-300">{r.source_service}</span>
              </>
            )}
            {r.reason && <span className="text-slate-500"> — {r.reason}</span>}
          </div>
          {r.changed_fields && Object.keys(r.changed_fields).length > 0 && (
            <div className="mt-1 text-xs font-mono bg-slate-950/60 border border-slate-800 rounded-lg p-2 space-y-0.5">
              {Object.entries(r.changed_fields).map(([f, val]) => {
                const pair = Array.isArray(val) ? val : [null, val];
                return (
                  <div key={f} className="text-slate-400">
                    <span className="text-slate-300">{f}</span>: {fmt(pair[0])}{" "}
                    <ArrowRight size={10} className="inline text-slate-600" /> {fmt(pair[1])}
                  </div>
                );
              })}
            </div>
          )}
          {r.endpoint && <div className="mt-0.5 text-[11px] text-slate-600 font-mono">{r.endpoint}</div>}
        </li>
      ))}
    </ol>
  );
}

function Section({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="mb-5">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-200 mb-2">
        {icon}
        {title}
      </div>
      {children}
    </div>
  );
}

export default function MatchPairComparison() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [userId, setUserId] = useState(searchParams.get("user_id") || "");
  const [candidateId, setCandidateId] = useState(searchParams.get("candidate_id") || "");
  const [data, setData] = useState<PairData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (uid: string, cid: string) => {
    if (!uid.trim() || !cid.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await GetMatchPairComparison(uid.trim(), cid.trim());
      if (res?.status === "success" && res?.data) {
        setData(res.data);
      } else {
        setData(null);
        setError(res?.message || res?.detail || "No records found for this pair.");
      }
    } catch (e: any) {
      setData(null);
      setError(e?.message || "Failed to load comparison.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-load if both IDs are present in the URL on mount
  useEffect(() => {
    const u = searchParams.get("user_id");
    const c = searchParams.get("candidate_id");
    if (u && c) load(u, c);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams({ user_id: userId.trim(), candidate_id: candidateId.trim() });
    load(userId, candidateId);
  };

  const fwd = data?.forward.matched_profile;
  const rev = data?.reverse.matched_profile;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-2 mb-1">
          <GitCompare className="text-indigo-400" />
          <h1 className="text-2xl font-bold">Match Pair Comparison</h1>
        </div>
        <p className="text-sm text-slate-500 mb-5">
          Compare the forward (A → B) and reverse (B → A) match — how each was added, by whom, and the full record across
          matched_profiles, match_making_queue, and the audit trail.
        </p>

        <form onSubmit={onSubmit} className="flex flex-wrap items-end gap-3 mb-6 bg-slate-900/60 border border-slate-800 rounded-2xl p-4">
          <div className="flex flex-col">
            <label className="text-xs text-slate-500 mb-1">User ID (A)</label>
            <input
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              placeholder="U12345"
              className="bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500 w-48"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-slate-500 mb-1">Candidate ID (B)</label>
            <input
              value={candidateId}
              onChange={(e) => setCandidateId(e.target.value)}
              placeholder="U67890"
              className="bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500 w-48"
            />
          </div>
          <button
            type="submit"
            disabled={loading || !userId.trim() || !candidateId.trim()}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg px-4 py-2 text-sm font-semibold"
          >
            <Search size={16} />
            {loading ? "Loading…" : "Compare"}
          </button>
        </form>

        {error && (
          <div className="bg-rose-950/40 border border-rose-800/50 text-rose-300 rounded-xl p-4 mb-6 flex items-center gap-2">
            <AlertTriangle size={18} />
            {error}
          </div>
        )}

        {data && (
          <>
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <PersonCard label="Side A (user_id)" person={data.people.a} />
              <div className="flex items-center justify-center text-slate-600">
                <GitCompare size={22} />
              </div>
              <PersonCard label="Side B (candidate_id)" person={data.people.b} />
            </div>

            {/* matched_profiles side-by-side */}
            <Section icon={<ListChecks size={16} className="text-indigo-400" />} title="matched_profiles (forward vs reverse)">
              {!fwd && !rev ? (
                <div className="text-sm text-slate-500 italic">No matched_profiles rows in either direction.</div>
              ) : (
                <div className="overflow-hidden rounded-2xl border border-slate-800">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-900/80 text-slate-400">
                      <tr>
                        <th className="text-left font-medium px-4 py-2 w-1/3">Field</th>
                        <th className="text-left font-medium px-4 py-2">{data.forward.direction}</th>
                        <th className="text-left font-medium px-4 py-2">{data.reverse.direction}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {MATCH_FIELDS.map(({ key, label }) => {
                        const isDate = key.endsWith("_at");
                        const fv = fwd ? fwd[key] : undefined;
                        const rv = rev ? rev[key] : undefined;
                        const differ =
                          fwd && rev && JSON.stringify(fv ?? null) !== JSON.stringify(rv ?? null) && !isDate;
                        return (
                          <tr
                            key={key}
                            className={`border-t border-slate-800/70 ${differ ? "bg-amber-500/5" : ""}`}
                          >
                            <td className="px-4 py-1.5 text-slate-500">{label}</td>
                            <td className={`px-4 py-1.5 ${differ ? "text-amber-300 font-medium" : "text-slate-200"}`}>
                              {fwd ? (isDate ? fmtDate(fv) : fmt(fv)) : <span className="text-slate-600">no row</span>}
                            </td>
                            <td className={`px-4 py-1.5 ${differ ? "text-amber-300 font-medium" : "text-slate-200"}`}>
                              {rev ? (isDate ? fmtDate(rv) : fmt(rv)) : <span className="text-slate-600">no row</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>

            {/* Queue + Audit per direction */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {[data.forward, data.reverse].map((dir, i) => (
                <div key={i} className="bg-slate-900/40 border border-slate-800 rounded-2xl p-4">
                  <div className="text-sm font-bold text-indigo-300 mb-4 font-mono">{dir.direction}</div>
                  <Section icon={<ListChecks size={16} className="text-indigo-400" />} title="match_making_queue">
                    <QueueBlock rows={dir.queue} />
                  </Section>
                  <Section icon={<History size={16} className="text-indigo-400" />} title="Audit timeline">
                    <AuditTimeline rows={dir.audit} />
                  </Section>
                </div>
              ))}
            </div>
          </>
        )}

        {!data && !error && !loading && (
          <div className="text-center text-slate-600 py-16">
            Enter a user ID and candidate ID to compare their match.
          </div>
        )}
      </div>
    </div>
  );
}
